import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { KeyRound } from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { z } from 'zod';

import { api, ensureOk } from '@/api/client';
import { mensajeDe } from '@/api/errors';
import { useStepUp } from '@/auth/StepUp';
import { Campo } from '@/components/Campo';
import { Modal } from '@/components/Modal';

import { claveAlumno, type AlumnoDetalle } from './queries';

const contrasenaSchema = z
  .object({
    password: z
      .string()
      .min(8, 'Escribe al menos 8 caracteres.')
      .max(128, 'Máximo 128 caracteres.'),
    confirmar: z.string(),
  })
  .refine((v) => v.password === v.confirmar, {
    message: 'Las contraseñas no coinciden.',
    path: ['confirmar'],
  });
type Contrasena = z.infer<typeof contrasenaSchema>;

/**
 * Define una contraseña nueva al alumno (exige step-up del staff y queda en la auditoría). Cierra
 * sus sesiones y le avisa por correo. El alumno no recibe ni escribe código 2FA por este cambio.
 */
export function CambiarContrasena({ a }: { a: AlumnoDetalle }) {
  const qc = useQueryClient();
  const { proteger, dialogo } = useStepUp();
  const [abierto, setAbierto] = useState(false);
  const form = useForm<Contrasena>({
    resolver: zodResolver(contrasenaSchema),
    defaultValues: { password: '', confirmar: '' },
  });

  const cerrar = () => {
    setAbierto(false);
    form.reset();
  };

  const cambiar = useMutation({
    mutationFn: (v: Contrasena) =>
      proteger(() =>
        ensureOk(
          api.POST('/api/v1/admin/users/{id}/password', {
            params: { path: { id: String(a.id) } },
            body: { password: v.password },
          }),
        ),
      ),
    onSuccess: () => {
      cerrar();
      toast.success('Contraseña cambiada. Cerramos sus sesiones abiertas.');
      void qc.invalidateQueries({ queryKey: claveAlumno(a.id) });
    },
    onError: (e) => toast.error(mensajeDe(e)),
  });

  const { errors } = form.formState;
  return (
    <>
      <button type="button" className="btn-cs btn-contorno" onClick={() => setAbierto(true)}>
        <KeyRound size={16} aria-hidden /> Cambiar contraseña
      </button>
      <Modal
        abierto={abierto}
        onCerrar={cerrar}
        titulo="Cambiar contraseña"
        descripcion={`Define una contraseña nueva para ${a.name}. Sus sesiones abiertas se cerrarán.`}
      >
        <form
          className="space-y-4"
          onSubmit={(e) => void form.handleSubmit((v) => cambiar.mutate(v))(e)}
          noValidate
        >
          <Campo etiqueta="Nueva contraseña" error={errors.password?.message}>
            <input
              type="password"
              autoComplete="new-password"
              className="input-vidrio"
              {...form.register('password')}
            />
          </Campo>
          <Campo etiqueta="Confirmar contraseña" error={errors.confirmar?.message}>
            <input
              type="password"
              autoComplete="new-password"
              className="input-vidrio"
              {...form.register('confirmar')}
            />
          </Campo>
          <p className="text-xs text-texto-3">
            Mínimo 8 caracteres. No puede ser su correo ni un mismo carácter repetido.
          </p>
          <div className="flex justify-end gap-2">
            <button type="button" className="btn-cs btn-contorno" onClick={cerrar}>
              Cancelar
            </button>
            <button type="submit" className="btn-cs btn-primario" disabled={cambiar.isPending}>
              {cambiar.isPending ? 'Guardando…' : 'Cambiar contraseña'}
            </button>
          </div>
        </form>
      </Modal>
      {dialogo}
    </>
  );
}
